import { checkUserMeetingPermissions } from '@adamdotai/core/utils'
import {
    Flex,
    HStack,
    Text,
    ThumbsDownIcon,
    ThumbsUpIcon,
    useToast,
} from '@adamdotai/design-system'
import { useMutation } from '@urql/next'
import { useTranslations } from 'next-intl'
import { FeedbackButton } from '@/components/AiFeedback/FeedbackButton'
import { FeedbackTooltip } from '@/components/AiFeedback/FeedbackTooltip'
import { graphql } from '@/generated/gql'
import { useMeeting } from '../../../../../../utils/state/meeting-room'

const SUBMIT_HIGHLIGHT_FEEDBACK =
    graphql(`mutation SubmitHighlightFeedback($input: SubmitSummaryFeedbackInput!){
    submitHighlightFeedback(input: $input)
  }`)

export const SummaryFeedback = () => {
    const [, submitHighlightFeedback] = useMutation(SUBMIT_HIGHLIGHT_FEEDBACK)
    const [meeting, setMeeting] = useMeeting()
    const toast = useToast()
    const hasSubmitFeedbackPermission = checkUserMeetingPermissions({
        permissions: meeting?.permissions,
        protectedAction: 'manageContentSpace',
    })
    const t = useTranslations()

    const handleSubmitFeedback = (text: string, isThumbsUp: boolean) => {
        submitHighlightFeedback({
            input: {
                meetingId: meeting?.id,
                isThumbsUp,
                feedbackMessage: text,
            },
        })
        toast({
            status: 'success',
            content: t('toast.thanks-sharing-feedback'),
        })
        // TODO: update the `hasSubmittedFeedback` solely when the meeting object is split into smaller atoms
        setMeeting({
            ...meeting,
            summary: {
                ...meeting?.summary,
                hasSubmittedFeedback: true,
            },
        })
    }

    const handleThumbsUp = (text: string) => {
        handleSubmitFeedback(text, true)
    }

    const handleThumbsDown = (text: string) => {
        handleSubmitFeedback(text, false)
    }

    if (meeting?.summary?.hasSubmittedFeedback || !hasSubmitFeedbackPermission)
        return null

    return (
        <Flex
            role="menu"
            aria-label="AI highlights feedback menu"
            w="full"
            justifyContent="space-between"
            px="5"
            py="3"
            alignItems="center"
            bgColor="neutral.50"
        >
            <Text size="body2" color="neutral.1600">
                {t('common.was-this-helpful')}
            </Text>
            <HStack>
                <FeedbackButton icon={ThumbsUpIcon} onSubmit={handleThumbsUp} />
                <FeedbackButton
                    icon={ThumbsDownIcon}
                    onSubmit={handleThumbsDown}
                />
                <FeedbackTooltip />
            </HStack>
        </Flex>
    )
}
